import React, {useState} from 'react';
import BaseModal from '../types/Base';
import {connect} from 'react-redux';
import {Tabs} from '../../../../shared';
import {_removeModal} from '../../../store';
import PropTypes from 'prop-types';
import styled from 'styled-components/macro';
import {PORTAL_NAME} from '../../../../config';

const _Content = styled.div`
  color: ${(props) => props.theme.dark ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.7)'};
`;

const general = props => <_Content>{props}</_Content>;

const privacyPolicy = <_Content>
  <p>This privacy policy (this &quot;POLICY&quot;) describes the collection of personal information by CircleBlack, Inc., a Delaware
    corporation (&quot;CIRCLEBLACK,&quot; &quot;WE,&quot; or &quot;US&quot;) from users of our Web site
    at <a id='privacyModal-externalLink-circleblack'
          target='_blank'
          href='http://CircleBlack.com'
          rel='noreferrer'>www.CircleBlack.com</a> (the
    &quot;WEBSITE&quot;), as well as all related applications, widgets, software, tools, and other services provided by us and on
    which a link
    to this Policy is displayed (collectively, together with the Website, our &quot;SERVICES&quot;). This Policy also describes our
    use and
    disclosure of such information. By using our Services, you consent to the collection, use, and disclosure of personal
    information in accordance with this Policy. This Policy is subject to the provisions of any CircleBlack Terms of Use that
    incorporate this Policy by reference. IF YOU DO NOT AGREE WITH THIS POLICY, PLEASE DO NOT USE THE WEBSITE.</p>
  <h3>Personal Information</h3>
  <p>&quot;Personal Information,&quot; as used in this Policy, is information that specifically identifies an individual, such as an
    individual&apos;s name, address, telephone number, or e-mail address. Personal Information also includes information about an
    individual’s activities, such as information about his or her activity on our Services, and demographic information, such as
    date of birth, gender, geographic area, and preferences, when any of this information is linked to Personal Information that
    identifies that individual.</p>
  <p>Personal Information does not include &quot;aggregate&quot; or other non-personally identifiable information. Aggregate
    information is
    information that we collect about a group or category of products, services, or users that is not personally identifiable or
    from which individual identities are removed. We may use and disclose aggregate information, and other non-personally
    identifiable information, for various purposes.</p>
  <p>Although information uploaded to the Service by a CircleBlack customer for processing (&quot;Customer Data&quot;) may include
    information
    that specifically identifies an individual, that information is not Personal Information for the purposes of this Policy. Please
    see the &quot;Customer Data and Security&quot; section below for information regarding CircleBlack’s handling of Customer
    Data.</p>
  <h3>Collection of Information</h3>
  <h4> Collection of Voluntarily-Provided Information</h4>
  <p>We collect Personal Information that our users provide to us in a variety of ways on our Services. These include the
    following: </p>
  <p><i>E-mail Newsletters.</i> We may offer e-mail newsletters from time to time on our Services. If you sign up to receive a
    newsletter from us, we collect your e-mail address.</p>
  <p><i>Correspondence</i> If you contact us by e-mail, using a contact form on the Services, or by mail, fax, or other means, we
    collect the Personal Information contained within, and associated with, your correspondence.</p>
  <h4> Passive Information Collection</h4>
  <p>When you visit our Services, some information is collected automatically. For illustrative purposes but not limited to: when
    you access our Services, we automatically collect your browser’s Internet Protocol (IP) address, browser type, the nature of the
    device from which you are visiting the Services (e.g., a tablet or a mobile device); the identifier for any handheld or mobile
    device that you may be using, the actions you take on our Services; the content, features, and activities that you access and
    participate in on our Services.</p>
  <p>We may collect this information passively using technologies such as standard server logs, cookies, and clear GIFs (also known
    as &quot;Web beacons&quot;). We use passively-collected information to administer, operate, and improve our Website, other
    services, and
    systems; to improve the effectiveness of advertising on our Services, and to provide advertisements and other content that is
    tailored to you. If we link or associate any information gathered through passive means with Personal Information, or if
    applicable laws require us to treat any information gathered through passive means as Personal Information, we treat the
    combined information as Personal Information under this Policy. Otherwise, we use and disclose information collected by passive
    means in aggregate form or otherwise in a non-personally identifiable form.</p>

  <h4>Google Analytics</h4>

  <p>We use a tool called &quot;Google Analytics&quot; to collect potentially Personal Information. The analytics features
    implemented are
    for <a id='privacyModal-externalLink-googleAnalyticsDemographicsAndInterests'
           target='_blank'
           href='https://support.google.com/analytics/answer/2799357?hl=en'
           rel='noreferrer'>demographic and interest reporting</a>. Users
    can <a id='privacyModal-externalLink-googleAnalyticsOptOut'
           target='_blank'
           href='https://tools.google.com/dlpage/gaoptout/'
           rel='noreferrer'>opt-out</a> of Google Analytics for Display Advertising and customize Google Display Network ads
    using <a id='privacyModal-externalLink-googleAnalyticsAdsSettings'
             target='_blank'
             href='https://www.google.com/settings/u/0/ads?hl=en&sig=ACi0TCjJn1o4MBMtGs7PuGXmQTMXL1pKVlmTP-2gwgG7JDIXKXAdxnVqfVu1qAPwqBTgFOLPWtZBuC830QOJwMIdYYTR-9x_pN9uU0zvFdBY8xQYI05RFBDjDa8NwXTHvGR27PKZxHQI5fcvYS8pECbbDN7o0bFWRKqSeLSTs7Tgqkxu4zxF3Og/'
             rel='noreferrer'>Ads Settings</a>.</p>

  <p>Google Analytics collects only the IP address assigned to you on the date you visit this site, rather than your name or other
    identifying information. We do not combine the information collected through the use of Google Analytics with personally
    identifiable information. Although Google Analytics uses cookies to identify you as a unique user the next time you visit this
    site, the cookie cannot be used by anyone but Google. You can prevent Google Analytics from recognizing you on return visits to
    this site by disabling cookies on your browser.</p>

  <p>Google’s ability to use and share information collected by Google Analytics is restricted by
    the <a id='privacyModal-externalLink-googleAnalyticsTerms'
           target='_blank'
           href='http://www.google.com/analytics/terms/us.html'
           rel='noreferrer'>Google Analytics Terms of Use</a> and the <a id='privacyModal-externalLink-googleAnalyticsPrivacy'
                                                                         target='_blank'
                                                                         href='http://www.google.com/policies/privacy/'
                                                                         rel='noreferrer'>Google Privacy Policy</a>.</p>

  <h4>Information from Other Sources</h4>
  <p>From time to time we may receive information about you, including Personal Information, from third parties, and may combine
    this information with other Personal Information we maintain about you. If we do so, this Policy governs any combined
    information that we maintain in personally identifiable format.</p>

  <h3>Use of Personal Information</h3>
  <p>We use Personal Information to provide services and information that you request; to enhance, improve, operate, and maintain
    our Services, our programs, services, Web sites, and other systems; to prevent fraudulent use of our Services and other systems;
    to prevent or take action against activities that are, or may be, in violation of our Terms of Use or applicable law; to tailor
    advertisements, content, and other aspects of your experience on and in connection with the Services; to maintain a record of
    our dealings with you; for other administrative purposes; and for any other purposes that we may disclose to you at the point at
    which we request your Personal Information, and pursuant to your consent.</p>

  <h3>Disclosure of Personal Information</h3>
  <p>Except as described in this Policy, we will not disclose your Personal Information that we collect on the Services to third
    parties without your consent. We may disclose information to third parties if you consent to us doing so, as well as in the
    following circumstances:</p>

  <h4>Service Providers</h4>
  <p>We may disclose Personal Information to third-party service providers (e.g., payment processing and data storage and processing
    facilities) that assist us in our work. We limit the Personal Information provided to these service providers to that which is
    reasonably necessary for them to perform their functions, and we require them to agree to maintain the confidentiality of such
    Personal Information.</p>

  <h4>Business Transfers</h4>
  <p>Information about our users, including Personal Information, may be disclosed and otherwise transferred to an acquirer, or
    successor or assignee as part of any merger, acquisition, debt financing, sale of CircleBlack assets, or similar transaction, as
    well as in the event of an insolvency, bankruptcy, or receivership in which Personal Information is transferred to one or more
    third parties as one of our business assets.</p>

  <h4>To Protect CircleBlack </h4>
  <p>We also disclose Personal Information if we believe that doing so is legally required, or is in our interest to protect our
    property or other legal rights (including, but not limited to, enforcement of our agreements), or the rights or property of
    others, or otherwise to help protect the safety or security of our Services and other users of the Services.</p>

  <p>We disclose Personal Information to third party advisors offering CircleBlack services to their own clients. We limit Personal
    Information to third party advisors to information about clients of those advisors, and we require them to agree to maintain the
    confidentiality of such Personal Information.</p>

  <h3>Access</h3>
  <p>If we offer the ability to create user accounts or profiles on our Services, you may have the ability to access and update many
    categories of personal information that you provide to us by logging in to your account and accessing your account settings. If
    you wish to access or amend any other personal information we hold about you, you may contact us
    at <a id='privacyModal-mailto-circleblackPrivacy'
          target='_blank'
          href='mailto:privacy@CircleBlack.com'
          rel='noreferrer'>privacy@CircleBlack.com</a>.</p>
  <p>If you request that we delete your account on any of our Services (via a user settings page, by e-mail, or otherwise), we will
    do so within a reasonable period of time, but we may need to retain some of your Personal Information in order to satisfy our
    legal obligations, or where we reasonably believe that we have a legitimate reason to do so.</p>
  <h3>Links</h3>
  <p>The Services may contain links to other Web sites, products, or services that we do not own or operate. For example, the
    Services may contain links to Third-Party Sites such as social networking services. If you choose to visit or use any
    Third-Party Sites or products or services available on or through such Third-Party Sites, this Policy will not apply to your
    activities or any information you disclose while using those Third-Party Sites or any products or services available on or
    through such Third-Party Sites. We are not responsible for the privacy practices of these Third-Party Sites or any products or
    services on or through them. Additionally, the Services may contain links to Web sites and services that we operate but that are
    governed by different privacy policies. We encourage you to carefully review the privacy policies applicable to any Web site or
    service you visit other than the Services before providing any Personal Information on them.</p>

  <h3>International Visitors</h3>
  <p>Our servers and data centers are located in the United States. If you choose to use the Services from outside the U.S., then
    you should know that you are transferring your Personal Information outside of your region and into the U.S. for storage and
    processing. By providing your Personal Information to us through your use of the Service, you agree to that transfer, storage,
    and processing in the U.S. Also, we may transfer your data from the U.S. to other countries or regions in connection with
    storage and processing of data, fulfilling your requests, and operating the Services. You should know that each region may have
    its own privacy and data security laws, some of which may be less stringent as compared to those of your own region.</p>

  <h3>Customer Data and Security</h3>
  <p>CircleBlack customers may electronically submit Customer Data to the Service for processing purposes. CircleBlack will not
    review, share, distribute, or reference any Customer Data except as provided in the license agreement between CircleBlack and
    the customer, or as may be required by law. In accordance with CircleBlack’s license agreements, CircleBlack may access Customer
    Data only for the purpose of providing the Service, preventing or addressing service or technical problems, at a customer’s
    request in connection with customer support matters, or as may be required by law.</p>
  <p>CircleBlack uses reasonable security measures that are designed to protect Personal Information and Customer Data from
    accidental loss, disclosure, misuse, and destruction. Please be aware, however, that no data security measures can be guaranteed
    to be completely effective. Consequently, we cannot ensure or warrant the security of any information that you or our enterprise
    customers provide to us. You transmit information to us at your own risk.</p>
</_Content>;

const termsConditions = <_Content>
  <p>The following agreement outlines your obligations when using the CircleBlack website available
    at <a id='termsModal-externalLink-circleblack'
          target='_blank'
          onClick='return cbOpenExternalLink()'
          href='http://circleblack.com' rel='noreferrer'>CircleBlack.com</a>, (the &quot;SITE&quot;). The Site is owned and operated
    by
    CircleBlack, Inc. (&quot;CIRCLEBLACK&quot;), and is accessed by you under the terms of use described below (&quot;TERMS OF
    USE&quot;). PLEASE
    READ THESE TERMS OF USE CAREFULLY BEFORE USING THE SERVICES. BY ACCESSING THE SITE OR ANY CONTENT ON THE SITE, YOU AGREE TO
    BECOME BOUND BY THESE TERMS AND CONDITIONS. IF YOU DO NOT AGREE TO ALL THE TERMS AND CONDITIONS, THEN YOU MAY NOT ACCESS THE
    SITE OR USE THE CONTENT OR ANY SERVICES IN THE SITE. CIRCLEBLACK&apos;S ACCEPTANCE IS EXPRESSLY CONDITIONED UPON YOUR ASSENT TO
    ALL OF THESE TERMS AND CONDITIONS, INCLUDING ANY TERMS AND CONDITIONS REFERENCED HEREIN, TO THE EXCLUSION OF ALL OTHER
    TERMS; IF THESE TERMS AND CONDITIONS ARE CONSIDERED AN OFFER BY CIRCLEBLACK, ACCEPTANCE IS EXPRESSLY LIMITED TO THESE TERMS
    AND CONDITIONS AND ANY TERMS AND CONDITIONS REFERENCED HEREIN.</p>

  <p>You agree that these terms and conditions supersede any prior version of the terms and conditions. CircleBlack reserves the
    right to change the terms and conditions by posting any revisions on the CircleBlack.com web site. In such event, your
    continued use of the Service will be governed by the terms as so amended.</p>

  <p>When using particular services provided by CircleBlack, you may be bound by further terms or services specific to those
    services, which may be posted and modified from time to time without notice to you. Such services may be provided through a
    third party, and may subject you to terms of service associated with that third party. All such terms and services are
    hereby incorporated by reference in their entirety.</p>

  <h4> 2. Modifications Of Terms Of Use</h4>
  <p>CircleBlack reserves the right, at its sole discretion, to modify or replace the Terms of Use at any time. If the
    alterations constitute a material change to the Terms of Use, CircleBlack will notify you by posting an announcement on the
    Site. What constitutes a &quot;material change&quot; will be determined at CircleBlack&apos;s sole discretion, in good faith and
    using
    reasonable judgment. You are responsible for reviewing and becoming familiar with any such modifications. Use of the Site by
    you following such notification constitutes your acceptance of the terms and conditions of the Terms of Use as modified.</p>

  <h4> 3. Content Ownership And Usage Rights</h4>
  <p>You agree that the CircleBlack Service may be used only for lawful purposes. You agree not to exploit, for any commercial
    purposes other than those purposes explicitly permitted, any portion of the CircleBlack Service, including content,
    advertisements, or software. You further agree not to reproduce, duplicate or copy any such content, advertisements, or
    software.</p>

  <p>You further agree not to use the Service through any interface other than a web browser or an alternative interface
    provided by CircleBlack for use in accessing such Service.</p>

  <p>You agree not to use a device, software, routine, or agent to interfere with the proper working of the Service, or with any
    activity being conducted therein. This includes uploading viruses or worms, spamming, phishing, stalking, harassing,
    impersonating, bullying, and otherwise harming users.</p>

  <p>You agree not to use a device, software, routine, or agent to impose a disproportionate load on the CircleBlack Service.
    This includes various hacking attacks designed to overload the Website or Service. You further agree not to probe, scan, or
    test the vulnerability of the Website or Service to breach security or authentication measures employed therein.</p>

  <p>You agree not to use a device, software, routine, or agent to navigate or search the Site other than those provided by
    CircleBlack. This includes spiders, robots, or avatars. You may not implement automated or non-automated &quot;scraping&quot; of
    content from the Website or Service.</p>

  <p>You agree not to use a device, software, routine, or agent to access data not intended for you, or to log into an account
    you are not otherwise authorized to access.</p>

  <p>Notwithstanding the above, operators of public search engines are hereby granted revocable permission to access and copy
    materials from publicly available searchable indices of material, but not cached or archived materials.</p>

  <h4>4. Content Ownership</h4>
  <p>Use of the Site may involve the use of proprietary CircleBlack data and software. If authorized to use the CircleBlack data
    and software, CircleBlack hereby grants you a personal, non-transferable, and non-exclusive license to use the object code
    or its software on a single computer, provided that you do not copy, modify, create a derivative work from, reverse
    engineer, or otherwise attempt to determine source code, sell, assign, sublicense, grant a security interest in, or
    otherwise, transfer any right in the software. You further agree not to modify the software in any manner, nor use modified
    versions of the software including for the purpose of obtaining unauthorized or duplicative access to services.</p>

  <p>You further acknowledge that the CircleBlack Services may use and implement software containing proprietary and
    confidential information protected by applicable intellectual property and other laws.</p>

  <p>You agree that content contained in information presented to you through the Website or Services may further be protected
    by copyright, trademark, service mark, patent, or other proprietary rights and laws. Except as expressly permitted by
    applicable law, or as authorized by CircleBlack or the applicable third party content licensor, you agree not to reproduce,
    modify, rent, lease, loan, sell, distribute, transmit, broadcast, publicly perform, or create derivative works based on the
    Site, such as content or software including source code or HTML code, in whole or in part.</p>

  <p>Accordingly, CircleBlack shall retain all worldwide rights in the intellectual property of the Site, including, but not
    limited to, trademarks, the &quot;look and feel&quot; of the Site, its color combinations, layout, and all other graphical
    elements,
    and the copyright in and to its original content. You should assume that everything you read or see on the Site is
    copyrighted or otherwise protected and owned by CircleBlack, or a third party who licensed the right to use such content to
    CircleBlack.</p>

  <h4> 5. Indemnity</h4>
  <p>You will indemnify and hold harmless CircleBlack, its parents, subsidiaries, affiliates, customers, vendors, officers and
    employees from any liability, damage or cost (including reasonable attorney&apos;s fees and costs) from any claim or demand made
    by any third party due to or arising out of your: access to the Site, use of the Services, violation of the Terms of Use by
    you, or the infringement by you of any intellectual property or other right of any person or entity.</p>

  <p>You further agree that CircleBlack is not responsible to you for any damage caused as a result of events beyond its
    reasonable control, including but not limited to acts of God, war, strikes, labor disputes, embargoes, government orders, or
    any other force majeure event.</p>

  <h4> 6. Warranty Disclaimers</h4>
  <p>Diligent care has been taken in acquiring and providing the information included and posted on the Site. Nonetheless,
    CircleBlack makes no guarantee or warranty, express or implied, as to the reliability, accuracy, timeliness or completeness
    of that information and assumes no responsibility for any errors or omissions therein. USER ACCESSES THIS SITE AT HIS OR HER
    OWN RISK. THE SITE IS PROVIDED ON AN &quot;AS IS, AS AVAILABLE&quot; BASIS WITHOUT WARRANTY OF ANY KIND AND ANY AND ALL
    WARRANTIES OF
    TITLE, MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, OR NON-INFRINGEMENT ARE SPECIFICALLY DISCLAIMED. FURTHER, ANY
    MATERIAL DOWNLOADED OR OTHERWISE OBTAINED FROM THE WEBSITE OR SERVICES ARE UTILIZED AT YOUR OWN RISK, AND YOU WAIVE ANY AND
    ALL CLAIMS AND CAUSES OF ACTION WITH RESPECT TO ANY DAMAGE TO YOUR COMPUTER SYSTEM, INTERNET ACCESS, DOWNLOAD OR DISPLAY
    DEVICE, OR ANY LOSS OF DATA THAT RESULTS FROM THE DOWNLOAD OF ANY SUCH MATERIAL. NEITHER CIRCLEBLACK NOR ITS AFFILIATES,
    EMPLOYEES, AGENTS OR THIRD PARTY CONTENT PROVIDERS SHALL BE LIABLE FOR ANY LOSS RESULTING FROM USE OR UNAVAILABILITY OF
    INFORMATION OR CONTENT ON THIS SITE, INCLUDING BUT NOT LIMITED TO ANY LOST PROFITS, LOSS OR DAMAGE TO DATA, OR ANY DIRECT,
    INDIRECT, SPECIAL, CONSEQUENTIAL, COMPENSATORY OR INCIDENTAL DAMAGES, EVEN IF THEY HAVE BEEN ADVISED OF THE POSSIBILITY OF
    SUCH DAMAGES. THIS DISCLAIMER IS APPLICABLE TO ANY DAMAGE OR INJURY RESULTING FROM NEGLIGENCE OR OMISSION OF CIRCLEBLACK,
    COMPUTER VIRUS OR OTHER SIMILAR ITEM, TELECOMMUNICATIONS ERRORS, OR UNAUTHORIZED ACCESS TO OR USE OF USER INFORMATION
    THROUGH THEFT OR ANY OTHER MEANS. CIRCLEBLACK IS NOT LIABLE FOR CRIMINAL, TORTUOUS, OR NEGLIGENT ACTIONS OR OMISSIONS OF
    THIRD PARTIES THAT AFFECT THIS SITE. IN NO EVENT WILL CIRCLEBLACK OR ANY OF ITS AFFILIATES, AGENTS, EMPLOYEES, ASSIGNS OR
    THIRD PARTY CONTENT PROVIDERS BE HELD LIABLE FOR ANY TORTUOUS OR ILLEGAL CONDUCT OF OTHER USERS. IN NO EVENT WILL
    CIRCLEBLACK OR ANY OF ITS AFFILIATES, AGENTS, EMPLOYEES OR ASSIGNS BE HELD LIABLE FOR ANY DAMAGE TO EQUIPMENT, HARDWARE OR
    OTHER PROPERTY OF USER OR PERSONAL INJURY THAT ARISES IN CONNECTION WITH USE OF THE SITE. THIS CLAUSE EXPLICITLY INCLUDES
    ANY DAMAGE DUE TO HACKING OR ANY OTHER MISUSE OF THE WEBSITE OR SERVICE.</p>

  <p>No statement obtained by you from CircleBlack shall create any warranty not expressly stated in the TOS.</p>

  <p>CircleBlack attempts to ensure the accuracy of the information as it is posted, but cannot guarantee that all of the
    information is accurate. CircleBlack has no responsibility for any third-party content referenced by the Service or
    accessible through the Service by hyperlink or otherwise.</p>

  <p>Some states or other jurisdictions do not allow the exclusion of implied warranties, and you may have other rights that
    vary from state to state or from jurisdiction to jurisdiction. Should you be a resident of a state or jurisdiction that
    requires a specific statement of release, you hereby waive any such provision of law in your state or jurisdiction. This
    includes a waiver of California Civil Code §1542 governing claims which are unknown at the time of the release, as well as
    any similar provision that has the same intent or effect as that provision.</p>

  <p>CircleBlack reserves the right to change the terms of this disclaimer by posting any revisions on the Site. In such event,
    your continued use of the Services will be governed by the terms as so amended.</p>

  <h4>7. Content From Advertisers And Other Third Parties</h4>
  <p>CircleBlack may provide access to third party resources through its Services, and advertisements shown on its Website and
    within the context of its Service. You agree that CircleBlack shall not be responsible or liable for any loss or damage of
    any sort incurred as a result of your dealings with, or the presence of, advertisers within the Website or Services. Your
    correspondence with, participation in promotions of, or contracts entered into with third parties found within the context
    of the Service are solely between you and the relevant third party. Accordingly, CircleBlack is not responsible for
    contracts created between you and third parties found on the Website or Services.</p>

  <p>You further acknowledge and agree that CircleBlack, or third parties accessible through the Service, may provide links to
    other websites or resources. You agree that CircleBlack is not responsible for the availability or content of such web sites
    or resources, and is not responsible or liable for any content, advertising, or products available on or from such websites
    or resources, nor is it responsible for any damage or loss caused by or in association with such websites or resources.</p>

  <h4> 8. Linking And Framing</h4>
  <p>CircleBlack does not object to links on third-party sites to the homepage of the Site in an appropriate context. However,
    &quot;framing&quot; or &quot;mirroring&quot; the Site or any of its content is prohibited without the prior written consent of
    CircleBlack. The
    Site may provide links to other sites or resources available on the Internet. Because CircleBlack has no control over such
    sites and resources, you acknowledge and agree that CircleBlack is not responsible for the availability of such external
    sites or resources, and does not endorse and is not responsible or liable for any content, advertising, products or other
    materials on or available from such sites or resources. You further acknowledge and agree that CircleBlack shall not be
    responsible or liable, directly or indirectly, for any damage or loss caused or alleged to be caused by or in connection
    with use of or reliance on such content, goods or services available on or through any such site or resource.</p>

  <h4>9. Trademarks And Trade Names</h4>
  <p>CircleBlack, graphics, logos, designs, page headers, button icons, scripts, and other service names are the trademarks and
    trade names of CircleBlack. CircleBlack&apos;s trademarks and trade names may not be used, including as part of trademarks
    and/or
    as part of domain names, in connection with any product or service in any manner that is likely to cause confusion.</p>

  <h4>10. Electronic Communications</h4>
  <p>We use reasonable security measures and take reasonable system, process and administrative precautions to protect the
    security and integrity of email and other electronic communications that you may send to us. Despite all these precautions,
    no method of transmission over the Internet is entirely secure and we cannot guarantee the confidentiality or security of
    the electronic communications or its contents. You transmit such information at your own risk and you should decide very
    carefully which information you want to send us via any electronic communication.</p>

  <h4>11. Security</h4>
  <p>Users are prohibited from violating or attempting to violate the security of the site. CircleBlack will investigate
    occurrences of possible violations and will cooperate with all applicable law enforcement authorities in prosecuting
    violators.</p>
  <h4>12. Miscellaneous</h4>

  <p>The Terms of Use constitutes the entire agreement between users of the Site and CircleBlack, and regarding the subject
    matter hereof. If you breach any term of the Terms of Use, CircleBlack may pursue any legal or equitable remedy available,
    including but not limited to, direct, consequential, and punitive damages and injunctive relief. CircleBlack&apos;s remedies are
    cumulative and not exclusive. Failure of CircleBlack to exercise any remedy or enforce any portion of the Terms of Use at
    any time shall not operate as a waiver of any remedy or of the right to enforce any portion of the Agreement at any time
    thereafter. Users of this Site are responsible for compliance with all applicable regulations and laws. Any dispute arising
    out of the Terms of Use shall be governed by the laws of New York, notwithstanding any conflicts of law principles. Any
    action relating to the Terms of Use must be filed and maintained in a court in the state of New York, USA, and users consent
    to exclusive jurisdiction and venue in such courts for such purpose. You further agree that there is no third party
    beneficiary to this agreement.</p>

  <h4>13. Severability</h4>
  <p>If any provision of this &quot;Terms of Use&quot; is held by a court of competent jurisdiction to be contrary to law, the
    provision
    will be deemed null and void, and the remaining provisions of this Agreement will remain in effect.</p>
</_Content>;

const dataSourcePartners = <_Content>
  <p>Market Data services provided by our partners,&nbsp;
    <a id='dataSourcesModal-externalLink-xignite'
       target='_blank'
       rel='noreferrer'
       onClick='return cbOpenExternalLink()'
       href='https://www.xignite.com/'>Xignite</a>
    &nbsp;and&nbsp;
    <a id='dataSourcesModal-externalLink-morningstar'
       target='_blank'
       rel='noreferrer'
       onClick='return cbOpenExternalLink()'
       href='https://www.morningstar.com/'>Morningstar</a>.
  </p>
</_Content>;

const Disclosures = props => {
  const {dispatchRemoveModal, content, generalDisclosures} = props;
  const {defaultTab} = content;

  const generalTab = {
    name: 'general',
    title: 'General',
    icon: null,
    content: general(generalDisclosures),
  };
  const privacyPolicyTab = {
    name: 'privacyPolicy',
    title: 'Privacy Policy',
    icon: null,
    content: privacyPolicy,
  };
  const termsConditionsTab = {
    name: 'termsConditions',
    title: 'Terms & Conditions',
    icon: null,
    content: termsConditions,
  };
  const dataSourcePartnersTab = {
    name: 'dataSourcePartners',
    title: 'Data Source Partners',
    icon: null,
    content: dataSourcePartners,
  };

  const tabs = generalDisclosures
    ? [generalTab, privacyPolicyTab, termsConditionsTab, dataSourcePartnersTab]
    : [privacyPolicyTab, termsConditionsTab, dataSourcePartnersTab];

  const [curTabIndex, setCurTabIndex] = useState(
    defaultTab
      ? tabs.findIndex(tab => tab.name === defaultTab)
      : 0
  );

  return (
    <BaseModal onClose={() => dispatchRemoveModal(content)} title={'Disclosures'} size={PORTAL_NAME === 'investor' ? 'md' : 'lg'}>
      <Tabs
        tabs={tabs}
        displayTabTitles={true}
        minTabWidth={'50'}
        tabWidth={'200'}
        defaultSelection={curTabIndex || 0}
        onChange={tabIndex => {
          setCurTabIndex(tabIndex);
        }}
      />
    </BaseModal>
  );
};

const mapStateToProps = state => ({
  generalDisclosures: state.product.brand.generalDisclosures,
});

const mapDispatchToProps = dispatch => ({
  dispatchRemoveModal: modal => {
    dispatch(_removeModal(modal));
  },
});

export default connect(mapStateToProps, mapDispatchToProps)(Disclosures);

Disclosures.propTypes = {
  dispatchRemoveModal: PropTypes.func.isRequired,
  content: PropTypes.shape({
    id: PropTypes.string.isRequired,
    componentName: PropTypes.string.isRequired,
    /**
     * Name of the tab to default to
     */
    defaultTab: PropTypes.string,
  }),
  /**
   * The brand's generalDisclosures
   */
  generalDisclosures: PropTypes.isRequired,
};
